package guide.kuki.app

import guide.kuki.app.engine.Block
import guide.kuki.app.engine.GridMode
import guide.kuki.app.engine.LatLng
import guide.kuki.app.engine.NameEntry
import guide.kuki.app.engine.RadialMap
import guide.kuki.app.engine.RadialMapProvider
import kotlinx.browser.window
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.launch
import org.w3c.dom.events.Event

/**
 * Web entry point. Loads the radial map engine and exposes a small
 * bridge object on `window.kuki` so the inline JS in index.html can
 * query blocks and search.
 */
private var engine: RadialMap? = null

fun main() {
    installBridge()
    MainScope().launch {
        val provider = RadialMapProvider()
        provider.loadNames()
        engine = provider.get(GridMode.MIN_100)
        window.dispatchEvent(Event("kuki-ready"))
    }
}

private fun installBridge() {
    val findBlock: (Double, Double, Int) -> String? = { lng, lat, level ->
        engine?.let { e ->
            val b = e.findBlock(LatLng(lat, lng))
            if (b.isValid) blockToJson(e, b, subBlockLevel = level.coerceIn(0, MAX_SUB_LEVEL)) else null
        }
    }
    val searchAddresses: (String) -> Array<String> = { q ->
        engine?.searchAddress(q)?.toTypedArray() ?: emptyArray()
    }
    val findBlockByAddress: (String) -> String? = { addr ->
        engine?.let { e ->
            val b = e.findBlockByAddress(addr) ?: return@let null
            // Honor the sub-letter count typed into the search box: no
            // sub-letters means no sub-block highlight, two means two, etc.
            val level = addressSubLevel(addr)
            if (b.isValid) blockToJson(e, b, subBlockLevel = level) else null
        }
    }
    val findBlockByCode: (String) -> String? = { code ->
        engine?.let { e ->
            val b = e.findBlockByCode(code) ?: return@let null
            if (b.isValid) blockToJson(e, b, subBlockLevel = codeSubLevel(code)) else null
        }
    }
    val namedRings: () -> String = {
        engine?.let { e ->
            e.namedRings().joinToString(",", "[", "]") { nameInfoObject(it.entry, it.code) }
        } ?: "[]"
    }
    js(
        """
        window.kuki = {
            findBlock: function (lng, lat, level) { return findBlock(lng, lat, level == null ? 1 : level); },
            searchAddresses: function (q) { return searchAddresses(q); },
            findBlockByAddress: function (addr) { return findBlockByAddress(addr); },
            findBlockByCode: function (code) { return findBlockByCode(code); },
            namedRings: function () { return namedRings(); }
        };
        """
    )
}

private const val ADJACENT_COUNT = 3
private const val ADJACENT_BASE_WIDTH = 1.2
private const val ADJACENT_MIN_WIDTH = 0.4
private const val ADJACENT_BASE_OPACITY = 0.6

private val CODE_PREFIX_REGEX = Regex("^(\\d+(?:ng|[kmnprtv])?[aeiou][12])([a-z]*)$", RegexOption.IGNORE_CASE)
private val ADDRESS_SUBLETTERS_REGEX = Regex("[a-zA-Z]+$")
private const val MAX_SUB_LEVEL = 3

private fun codeSubLevel(code: String): Int {
    val match = CODE_PREFIX_REGEX.find(code.trim()) ?: return 0
    return match.groupValues[2].length.coerceAtMost(MAX_SUB_LEVEL)
}

private fun addressSubLevel(address: String): Int {
    val firstWord = address.trim().split(" ").firstOrNull() ?: return 0
    val match = ADDRESS_SUBLETTERS_REGEX.find(firstWord) ?: return 0
    return match.value.length.coerceAtMost(MAX_SUB_LEVEL)
}

private fun stripSubLetters(code: String): String {
    val match = CODE_PREFIX_REGEX.find(code) ?: return code
    return match.groupValues[1]
}

private fun blockToJson(e: RadialMap, block: Block, subBlockLevel: Int = 1): String {
    val sub1 = if (subBlockLevel >= 1) block.resolveSubBlock() else null
    val sub2 = if (subBlockLevel >= 2 && sub1 != null && sub1.isValid) sub1.resolveSubBlock() else null
    val sub3 = if (subBlockLevel >= 3 && sub2 != null && sub2.isValid) sub2.resolveSubBlock() else null
    val center = block.resolveLatLng()
    val baseAddress = e.getAddress(block)
    val subLetters = when {
        sub3 != null && sub3.isValid -> sub3.subLetters
        sub2 != null && sub2.isValid -> sub2.subLetters
        sub1 != null && sub1.isValid -> sub1.subLetters
        else -> ""
    }
    val displayAddress = if (baseAddress.isBlank()) "" else {
        val withSub = if (subLetters.isNotEmpty()) {
            val parts = baseAddress.split(" ", limit = 2)
            if (parts.size == 2) "${parts[0]}${subLetters} ${parts[1]}" else baseAddress
        } else baseAddress
        "$withSub Blk"
    }

    val blockRing = polygonRingJson(block.getPolygon())
    val subRing = if (sub1 != null && sub1.isValid) polygonRingJson(sub1.getPolygon()) else "null"
    val sub2Ring = if (sub2 != null && sub2.isValid) polygonRingJson(sub2.getPolygon()) else "null"
    val sub3Ring = if (sub3 != null && sub3.isValid) polygonRingJson(sub3.getPolygon()) else "null"
    val sliceRings = block.getSlicePolygon().joinToString(",") { polygonRingJson(it) }
    val adjacent = adjacentEdgesJson(e, block)
    val nameInfo = nameInfoJson(e, block)
    val rawCode = e.getBlockCode(block)
    val code = when {
        sub3 != null && sub3.isValid -> stripSubLetters(rawCode) + sub3.subLetters
        sub2 != null && sub2.isValid -> stripSubLetters(rawCode) + sub2.subLetters
        subBlockLevel == 0 -> stripSubLetters(rawCode)
        else -> rawCode
    }

    return """{
        "blockRing":$blockRing,
        "subBlockRing":$subRing,
        "subBlock2Ring":$sub2Ring,
        "subBlock3Ring":$sub3Ring,
        "sliceRings":[$sliceRings],
        "adjacentEdges":[$adjacent],
        "centerLng":${center.lng},
        "centerLat":${center.lat},
        "address":${jsonString(displayAddress)},
        "code":${jsonString(code)},
        "nameInfo":$nameInfo,
        "subBlockLevel":$subBlockLevel
    }""".trimIndent()
}

/**
 * Cultural-name info for [block]'s ring as a JSON object, or `null` when
 * the ring is not curated. Names attach to rings, so every block in a
 * ring carries the same `nameInfo`. Consumed by the index.html info box,
 * name sidebar and logo swap.
 */
private fun nameInfoJson(e: RadialMap, block: Block): String {
    val info = e.nameInfo(block) ?: return "null"
    val ringCode = e.ringCode(block) ?: return "null"
    return nameInfoObject(info, ringCode)
}

/**
 * JSON object form of a curated ring's [NameEntry] — shared by the
 * per-block `nameInfo` and the `namedRings` list bridge.
 */
private fun nameInfoObject(info: NameEntry, ringCode: String): String = """{
        "name":${jsonString(info.name)},
        "en":${jsonString(info.en)},
        "rar":${jsonString(info.rar)},
        "story":${jsonString(info.story)},
        "image":${jsonString(info.image)},
        "ringCode":${jsonString(ringCode)}
    }""".trimIndent()

/**
 * Inner and outer circumferential edges of [ADJACENT_COUNT] adjacent
 * blocks on each side along the arc, with width and opacity fading
 * by distance from the centre block.
 */
private fun adjacentEdgesJson(map: RadialMap, block: Block): String {
    val slice = block.slice ?: return ""
    val total = map.totalRadials
    val features = mutableListOf<String>()
    for (offset in 1..ADJACENT_COUNT) {
        val fade = (ADJACENT_COUNT - offset + 1).toDouble() / ADJACENT_COUNT
        val opacity = ADJACENT_BASE_OPACITY * fade
        val width = (ADJACENT_BASE_WIDTH * fade).coerceAtLeast(ADJACENT_MIN_WIDTH)
        for (sign in intArrayOf(-1, 1)) {
            val sliceIdx = ((slice.idx + sign * offset) % total + total) % total
            val adj = map.getSlice(sliceIdx).getBlock(block.idx)
            if (!adj.isValid) continue
            features.add(edgeFeatureJson(adj.pts[0], adj.pts[2], width, opacity))
            features.add(edgeFeatureJson(adj.pts[1], adj.pts[3], width, opacity))
        }
    }
    return features.joinToString(",")
}

private fun edgeFeatureJson(
    p0: guide.kuki.app.engine.Coords,
    p1: guide.kuki.app.engine.Coords,
    width: Double,
    opacity: Double,
): String = """{"line":[[${p0.lon},${p0.lat}],[${p1.lon},${p1.lat}]],"width":$width,"opacity":$opacity}"""

private fun polygonRingJson(points: List<DoubleArray>): String {
    if (points.isEmpty()) return "null"
    val coords = points.joinToString(",") { "[${it[1]},${it[0]}]" }
    val first = points.first()
    return "[$coords,[${first[1]},${first[0]}]]"
}

private fun jsonString(s: String): String =
    "\"" + s.replace("\\", "\\\\").replace("\"", "\\\"") + "\""
